import React, { useEffect, useState } from "react";
import { Crown, X } from "lucide-react";
import api from "../lib/api";
import { beginPlusPurchase } from "../lib/beginPlusPurchase";
import { hasToken as _hasToken } from "../lib/nativeTokenStorage";
import RestorePurchasesButton from "./RestorePurchasesButton";
import { trackMetaFunnelEvent } from "../lib/metaFunnelEvents";
// Iter XX.267 — Gully Pro pricing anchor. Non-purchasable Coming Soon
// tier surfaced inside the universal Plus paywall. Does not modify the
// existing Plus purchase flow, entitlements or analytics.
import ProTierCard from "./ProTierCard";
import { trackProEvent } from "../lib/proWaitlist";
// Iter XX.267b — light haptic on the paywall Plus CTA.
import { hapticLight } from "../lib/haptics";
// Iter XX.270 — "Build Your Gully" narrative (Phase 1). Additive,
// visibility-only subhead + impression event above the hero
// headline. Gated by `gully_narrative_enabled`.
import { useFlag } from "../lib/featureFlags";
import { trackFunnel } from "../lib/activationFunnel";

/**
 * Paywall — triggered when /api/web/upload-video or /api/web/chat
 * returns 402, and on /chat mount for non-entitled users (Iter XX.72).
 *
 * Two surfaces:
 *   • surface="video_paywall"           (legacy — 1 free video used)
 *   • surface="universal_plus_paywall"  (Iter XX.72 hard gate)
 *
 * The universal surface (Iter XX.72.3) is a long-form, emotional
 * narrative — Hero + 4 sections + Real Coaching + final CTA. It reads
 * like the beginning of a coaching journey, NOT like a subscription
 * page. Feature language is deliberately absent. Founder anchor:
 *   "Most cricketers play another season. Gully helps you become a
 *    different cricketer by the end of it."
 *
 * Funnel events (via /api/web/track):
 *   paywall_view, paywall_dismissed, upgrade_clicked,
 *   pass_redeem_started, sign_in_clicked.
 */
export default function PaywallModal({
  sessionId,
  freeReviewUsed = 3,
  freeReviewLimit = 3,
  // Iter XX.72 — Universal Plus Paywall surface. When set to
  // "universal_plus_paywall" the modal renders the Sell-The-Future
  // narrative (Iter XX.72.3) and is a HARD gate (no dismiss / close /
  // backdrop-close). The legacy video paywall retains its own copy.
  surface = "video_paywall",
  reason,
  message,
  upgrade_url = "/upgrade?from=paywall",
  onClose,
  onRequireIdentity,
}) {
  const isUniversal = surface === "universal_plus_paywall";
  // Iter XX.270 — "Build Your Gully" paywall narrative subhead.
  // Renders only on the universal surface and only when the
  // `gully_narrative_enabled` flag is ON. Fires the impression
  // event once per session.
  const gullyNarrativeOn = useFlag("gully_narrative_enabled");
  const showNarrativeSubhead = Boolean(isUniversal && gullyNarrativeOn);
  useEffect(() => {
    if (!showNarrativeSubhead) return;
    try {
      trackFunnel("gully_narrative_paywall_seen", { surface });
    } catch { /* fire-and-forget */ }
  }, [showNarrativeSubhead, surface]);

  // Iter 70 — localized pricing (legacy surface only).
  // Iter XX.267a — on native iOS, also read RevenueCat's localized
  // `priceString` so paywalls don't render blank when Stripe's country
  // bucket has no display value for the caller.
  const [priceLine, setPriceLine] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/plus/pricing")
      .then((r) => {
        if (cancelled) return;
        const local = r?.data?.local || {};
        const display = local.display;
        const period = local.period || "month";
        if (display) setPriceLine(`${display} / ${period} · cancel anytime`);
      })
      .catch(() => { /* silent */ });
    // Native RC fallback — only fires when running inside Capacitor.
    (async () => {
      try {
        const { isNative } = await import("../lib/nativePlatform");
        if (!isNative || !isNative()) return;
        const rc = await import("../lib/revenueCat");
        // Iter XX.267d — canonical resolver (cache → offering →
        // StoreKit-direct). Prefer whichever source resolves first.
        const resolved = await rc.resolveDisplayPrice("plus_monthly_ios");
        if (cancelled || !resolved?.priceString) return;
        try {
          // eslint-disable-next-line no-console
          console.log("[XX.267d-price] PaywallModal resolveDisplayPrice", {
            source:       resolved.source,
            price_string: resolved.priceString,
          });
        } catch { /* noop */ }
        setPriceLine(`${resolved.priceString} / month · cancel anytime`);
      } catch { /* keep whatever Stripe returned */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    api.post("/web/track", {
      session_id: sessionId,
      event: "paywall_view",
      payload: { trigger: isUniversal ? "chat_gate" : "upload_attempt",
                 surface, reason },
    }).catch(() => {});
    // Iter XX.198 — canonical acquisition funnel emission + Meta CAPI mirror.
    trackMetaFunnelEvent("plus_paywall_viewed", {
      session_id: sessionId,
      milestone_key: surface,
      extra: { surface, reason: reason || null,
               trigger: isUniversal ? "chat_gate" : "upload_attempt" },
    });
    // Iter XX.267 — additive `pricing_viewed` beacon (same event name
    // used on /upgrade), so we can measure paywall vs page pricing
    // exposure uniformly. Never renames or replaces existing events.
    if (isUniversal) {
      trackProEvent("pricing_viewed", "paywall_modal", sessionId);
    }
  }, [sessionId, surface, reason, isUniversal]);

  function dismiss() {
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId, event: "paywall_dismissed",
        payload: { surface },
      }).catch(() => {});
    }
    onClose?.();
  }

  function hasToken() {
    try { return _hasToken(); } catch { return false; }
  }

  function upgrade() {
    // Iter XX.267b — light haptic the moment the CTA registers.
    hapticLight();
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId, event: "upgrade_clicked",
        payload: { surface, source: "paywall_modal",
                   has_identity: hasToken() },
      }).catch(() => {});
    }
    // Iter XX.267 — additive `plus_cta_clicked` mirror so we can
    // measure Plus CTA rate uniformly across paywall + /upgrade.
    trackProEvent("plus_cta_clicked", "paywall_modal", sessionId);
    if (!hasToken()) {
      // Anonymous: hand off to Chat.jsx identity capture, which will
      // land back at /upgrade?from=paywall&autostart=1 and finish the
      // purchase via beginPlusPurchase() on that page.
      onRequireIdentity?.();
      return;
    }
    // Iter XX.72.6 — authed users use the canonical purchase flow
    // directly from the paywall so the funnel is instrumented and
    // server-side dedupe applies. Note: pricing lives on Upgrade.jsx;
    // beginPlusPurchase falls through to /billing/checkout with the
    // country omitted here (backend re-detects from request headers).
    beginPlusPurchase({ entryPoint: "paywall_modal", priceLoaded: true });
  }

  // Iter XX.72.1 — Pass redemption entry point (universal only).
  const [showPassEntry, setShowPassEntry] = useState(false);
  const [passCode, setPassCode] = useState("");
  const [passErr, setPassErr] = useState("");

  function goRedeemPass() {
    const raw = (passCode || "").trim();
    if (!raw) { setPassErr("Enter your pass code."); return; }
    let code = raw;
    try {
      if (raw.includes("/pass/")) code = raw.split("/pass/").pop().split(/[/?#]/)[0];
    } catch { /* leave as-is */ }
    code = code.replace(/^\/+|\/+$/g, "");
    if (!code) { setPassErr("Enter your pass code."); return; }
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId, event: "pass_redeem_started",
        payload: { source: "universal_paywall" },
      }).catch(() => {});
    }
    window.location.href = `/pass/${encodeURIComponent(code)}`;
  }

  function goSignIn() {
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId, event: "sign_in_clicked",
        payload: { source: "universal_paywall" },
      }).catch(() => {});
    }
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?return_to=${returnTo}`;
  }

  // Iter XX.205 — Sell-The-Future content REPLACED with truthful,
  // personalised proof-first structure. Reads `your_cricket` from
  // /entitlement/status?session_id=... (payload extension, no new
  // endpoint). Falls back gracefully when evidence is unavailable.
  const [yourCricket, setYourCricket] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!isUniversal) return;
    const url = sessionId
      ? `/web/entitlement/status?session_id=${encodeURIComponent(sessionId)}`
      : "/web/entitlement/status";
    api.get(url)
      .then((r) => {
        if (cancelled) return;
        const yc = r?.data?.your_cricket || null;
        if (yc && (yc.current_focus || yc.observation)) setYourCricket(yc);
      })
      .catch(() => { /* silent — generic hero renders */ });
    return () => { cancelled = true; };
  }, [sessionId, isUniversal]);

  const heroHeadline = "This is your cricket, remembered.";

  // Iter XX.71.5 — Real Coaching examples (reflection / pattern
  // recognition / understanding — no generic tips, no motivation).
  // Iter XX.205 — trimmed from 3 → 2 for a shorter paywall.
  const coachingPreviews = [
    {
      you: "Third match in a row I got out driving early. I don't know why.",
      gully: "You've mentioned early drives twice now — first vs the new ball, now again. Pattern: your front foot is committing before you've read the length. Want to look at where your weight is at ball-release?",
    },
    {
      you: "I feel like I'm losing form.",
      gully: "Let's not jump to that yet. In the last 4 innings you scored 12, 8, 34, 21. Not great, but the 34 was against a top attack. What's changed in your routine over the last two weeks?",
    },
  ];

  // -------------------- LEGACY VIDEO PAYWALL --------------------
  if (!isUniversal) {
    // Iter XX.275 — outcome-first bullets. Founder-brief rewrite:
    // lead with the cricket outcome, not the feature.
    const items = [
      "Keep every video review in one connected picture of your cricket",
      "See recurring problems across your videos, not just one clip",
      "Return to what you noticed last time and continue",
      "Unlimited video reviews",
    ];
    return (
      <div
        className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center"
        onClick={dismiss}
        data-testid="paywall-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl px-6 pt-8 pb-6"
          style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}
        >
          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="paywall-close"
            className="absolute top-4 right-4 p-1 text-neutral-500 hover:text-neutral-300"
          >
            <X size={16} />
          </button>
          <div className="text-center" data-testid="paywall-content">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-950/50 border border-emerald-900/50 text-emerald-400 mb-5">
              <Crown size={26} strokeWidth={1.5} />
            </div>
            <h2
              className="font-display text-2xl sm:text-3xl font-light tracking-tight text-neutral-50 mb-3"
              data-testid="paywall-headline"
            >
              Keep the review going after today’s video.
            </h2>
            <p className="text-[15px] text-neutral-300 leading-relaxed mb-1">
              Free Gully helps you understand <span className="text-emerald-300">today’s video</span>.
            </p>
            <p className="text-[15px] text-neutral-300 leading-relaxed mb-3">
              Plus helps you keep working on <span className="text-emerald-300">what it shows</span> — through the season.
            </p>
            <p className="text-sm text-neutral-500 leading-relaxed">
              You&apos;ve used your {freeReviewLimit} free reviews — Plus keeps
              your video work going between sessions.
            </p>
          </div>
          <ul className="text-left space-y-2 mt-5 mb-6 text-sm" data-testid="paywall-bullets">
            {items.map((i) => (
              <li key={i} className="flex items-start gap-2 text-neutral-300">
                <span className="text-emerald-400 mt-1.5 inline-block w-1 h-1 rounded-full bg-emerald-400 flex-none" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={upgrade}
            data-testid="paywall-upgrade-btn"
            className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors"
          >
            Continue with Gully Plus
          </button>
          {priceLine && (
            <p className="text-center text-xs text-neutral-500 mt-2" data-testid="paywall-price-line">
              {priceLine}
            </p>
          )}
          <button
            onClick={dismiss}
            data-testid="paywall-dismiss-btn"
            className="w-full py-2.5 mt-2 rounded-xl text-neutral-500 hover:text-neutral-300 text-sm transition-colors"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  // -------------------- UNIVERSAL PAYWALL (Iter XX.72.3) --------------------
  const sectionDivider = (
    <div className="my-10 flex justify-center">
      <div className="h-px w-16 bg-neutral-800" />
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex justify-center overflow-y-auto"
      onClick={() => { /* hard gate: backdrop no-op */ }}
      data-testid="paywall-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-universal-scroll"
        className="relative w-full sm:max-w-xl bg-black text-neutral-100 min-h-screen"
        style={{
          // Iter XX.287 — Full-screen paywall variant. Reserve the
          // <MobileBottomNav>'s measured height (which includes the
          // iOS safe-area inset) so the primary "Continue to plan"
          // CTA at the bottom of the scroll region is never behind
          // the persistent nav on iPhone Safari. Falls back to raw
          // safe-area inset when the nav isn't mounted.
          paddingBottom: "calc(3rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))",
          // Iter XX.267a — respect iOS Dynamic Island / status bar so
          // the hero doesn't collide with the notch on physical iPhone.
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        {/* ================== HERO ================== */}
        <section className="px-6 pt-16 sm:pt-20" data-testid="paywall-hero">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-950/60 border border-emerald-900/60 text-emerald-400 mb-8">
            <Crown size={22} strokeWidth={1.5} />
          </div>
          {showNarrativeSubhead && (
            <div
              data-testid="paywall-narrative-subhead"
              className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/90 font-semibold mb-4"
            >
              Keep working on your cricket — with Gully alongside you.
            </div>
          )}
          <h1
            data-testid="paywall-headline"
            className="font-display text-3xl sm:text-[2.5rem] leading-tight font-light tracking-tight text-neutral-50 mb-6"
          >
            {heroHeadline}
          </h1>
          {/* Iter XX.205 — Personalised proof block. Renders only when
              the backend attached truthful evidence. No fabricated
              placeholders. */}
          {yourCricket ? (
            <div className="space-y-4" data-testid="paywall-personalised-proof">
              {yourCricket.current_focus && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-500/90">Current focus</div>
                  <div className="mt-0.5 text-[15px] text-neutral-100 leading-relaxed" data-testid="paywall-focus">
                    {yourCricket.current_focus}
                  </div>
                </div>
              )}
              {yourCricket.observation && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-500/90">What we&apos;ve found</div>
                  <div className="mt-0.5 text-[15px] text-neutral-100 leading-relaxed" data-testid="paywall-observation">
                    {yourCricket.observation}
                  </div>
                </div>
              )}
              {yourCricket.next_step && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-500/90">Your next step</div>
                  <div className="mt-0.5 text-[15px] text-neutral-100 leading-relaxed" data-testid="paywall-next-step">
                    {yourCricket.next_step}
                  </div>
                </div>
              )}
              <p className="pt-2 text-[14px] text-neutral-400 leading-relaxed" data-testid="paywall-value-line">
                You&apos;ve started building a picture of your cricket.
                With Plus, Gully keeps building on it — across every
                match, practice, video and conversation.
              </p>
            </div>
          ) : (
            <div className="text-[15px] text-neutral-300 leading-relaxed" data-testid="paywall-generic-hero">
              <p>
                Your cricket lives across many matches, practices and
                conversations. Gully keeps it in one place, between
                sessions.
              </p>
              <p className="mt-3 text-neutral-400">
                Plus is what keeps you working on it, week after week.
              </p>
            </div>
          )}
        </section>

        {sectionDivider}

        {/* ================== PROOF — real coaching examples ================== */}
        <section className="px-6" data-testid="paywall-coaching-previews">
          <h2 className="font-display text-2xl font-light tracking-tight text-neutral-50 mb-2 leading-snug">
            How Gully coaches — an example
          </h2>
          {/* Iter XX.275 — dialogue below is a generic example, NOT a
              personalized reveal. Explicitly framed so we never imply
              this came from the current player's history. */}
          <p className="text-[12px] text-neutral-500 mb-5 leading-relaxed" data-testid="paywall-coaching-previews-disclaimer">
            Example only — not from your history.
          </p>
          <div className="space-y-4">
            {coachingPreviews.map((c, idx) => (
              <div
                key={idx}
                data-testid={`paywall-coaching-preview-${idx}`}
                className="rounded-2xl border border-neutral-900 bg-neutral-950/60 p-4"
              >
                <div className="text-[13px] text-neutral-400 mb-2.5 leading-relaxed">
                  <span className="text-neutral-600 mr-1.5 text-[11px] uppercase tracking-widest">You</span>
                  <br />
                  {c.you}
                </div>
                <div className="text-[14px] text-emerald-100/95 leading-relaxed">
                  <span className="text-emerald-400 mr-1.5 text-[11px] uppercase tracking-widest">Gully</span>
                  <br />
                  {c.gully}
                </div>
              </div>
            ))}
          </div>
        </section>

        {sectionDivider}

        {/* ================== PRO — COMING SOON ================== */}
        {/* Iter XX.267 — Gully Pro pricing anchor. Non-purchasable
            $99/month "Coming Soon" tier with waitlist CTA. Renders
            below the coaching-previews proof so Plus retains the
            strongest CTA. Never modifies purchase / entitlement flow. */}
        <section className="px-6" data-testid="paywall-pro-section">
          <ProTierCard source="paywall_modal" sessionId={sessionId} />
        </section>

        {sectionDivider}

        {/* ================== FINAL CTA ================== */}
        <section className="px-6" data-testid="paywall-final-cta">
          <button
            onClick={upgrade}
            data-testid="paywall-upgrade-btn"
            className="w-full py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-[15px] font-medium transition-colors"
          >
            Keep Gully learning my cricket
          </button>
          {priceLine && (
            <p className="text-center text-xs text-neutral-500 mt-3" data-testid="paywall-price-line">
              {priceLine}
            </p>
          )}
          {/* Iter XX.275 — soft coaching anchor. No universal price claim;
              positions Plus as a between-sessions companion. */}
          <p
            className="text-center text-[11px] text-neutral-500 mt-2 leading-relaxed"
            data-testid="paywall-coaching-anchor"
          >
            Private coaching happens session by session. Gully Plus stays
            with your cricket between sessions.
          </p>

          <div className="mt-5 space-y-2" data-testid="paywall-secondary-actions">
            {/* Iter XX.197 — Native-only Restore Purchases surface.
                Renders nothing on web (Stripe restores itself). */}
            <RestorePurchasesButton
              onRestored={() => { try { onClose?.(); } catch { /* noop */ } }}
            />
            {!showPassEntry ? (
              <button
                type="button"
                onClick={() => { setShowPassEntry(true); setPassErr(""); }}
                data-testid="paywall-redeem-pass-btn"
                className="w-full py-3 rounded-2xl border border-neutral-800 text-neutral-300 text-sm hover:bg-neutral-900 transition-colors"
              >
                Redeem Gully Pass
              </button>
            ) : (
              <div
                className="rounded-2xl border border-neutral-800 bg-neutral-950/60 p-4"
                data-testid="paywall-pass-entry"
              >
                <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">
                  Your Gully Pass code
                </label>
                <input
                  type="text"
                  autoFocus
                  value={passCode}
                  onChange={(e) => { setPassCode(e.target.value); setPassErr(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") goRedeemPass(); }}
                  placeholder="e.g. gp_abcd1234"
                  data-testid="paywall-pass-input"
                  className="w-full px-3 py-2 rounded-lg bg-black border border-neutral-800 text-neutral-100 text-sm focus:outline-none focus:border-emerald-700"
                />
                {passErr && (
                  <p className="text-xs text-red-400 mt-1.5" data-testid="paywall-pass-error">
                    {passErr}
                  </p>
                )}
                <div className="flex gap-2 mt-2.5">
                  <button
                    type="button"
                    onClick={goRedeemPass}
                    data-testid="paywall-pass-submit"
                    className="flex-1 py-2 rounded-lg bg-emerald-700/80 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
                  >
                    Redeem
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPassEntry(false); setPassCode(""); setPassErr(""); }}
                    data-testid="paywall-pass-cancel"
                    className="px-3 py-2 rounded-lg text-neutral-500 hover:text-neutral-300 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={goSignIn}
              data-testid="paywall-signin-btn"
              className="w-full py-3 rounded-2xl text-neutral-400 hover:text-neutral-200 text-sm transition-colors"
            >
              Already have an account? Sign In
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
